/// <reference path="../../types/tweakwise-layer.d.ts" />

class TweakwiseTracker {
  /**
   * Check if tweakwiseLayer is available and initialized
   */
  private isAvailable(): boolean {
    return typeof window !== 'undefined' && 
           typeof window.tweakwiseLayer !== 'undefined' && 
           Array.isArray(window.tweakwiseLayer);
  }

  /**
   * Safely push an event to tweakwiseLayer
   * Only pushes if the layer is available (script loaded and not a crawler)
   */
  private pushEvent(event: TweakwiseEvent): void {
    if (!this.isAvailable()) {
      console.warn('Tweakwise tracking not available (script not loaded or crawler detected)');
      return;
    }

    window.tweakwiseLayer!.push(event);
  }

  /**
   * Track page impression - when multiple products are viewed on a page
   * @param data - Page impression data
   */
  trackPageImpression(data: {
    requestId?: string;
    itemIds: string[];
    profileKey?: string;
    sessionKey?: string;
  }): void {
    this.pushEvent({
      event: 'pageImpression',
      data,
    });
  }

  /**
   * Track item click - when a user clicks on a product
   *
   * @param data - Item click data
   */
  trackItemClick(data: {
    requestId: string;
    itemId: string;
    sessionKey?: string;
  }): void {
    this.pushEvent({
      event: 'itemClick',
      data,
    });
  }

  /**
   * Track add to cart - when a user adds a product to cart
   * @param data - Add to cart data
   */
  trackAddToCart(data: {
    requestId?: string;
    productKey: string;
    quantity?: number;
    totalAmount?: number;
    profileKey?: string;
    sessionKey?: string;
  }): void {
    this.pushEvent({
      event: 'addToCart',
      data,
    });
  }

  /**
   * Track add to wishlist - when a user adds a product to wishlist
   * @param data - Add to wishlist data
   */
  trackAddToWishlist(data: {
    requestId?: string;
    itemId: string;
    profileKey?: string;
    sessionKey?: string;
  }): void {
    this.pushEvent({
      event: 'addToWishlist',
      data,
    });
  }

  /**
   * Track page view - when a single product page is viewed
   * @param data - Page view data
   */
  trackPageView(data: {
    itemId: string;
    profileKey?: string;
    sessionKey?: string;
  }): void {
    this.pushEvent({
      event: 'pageView',
      data,
    });
  }

  /**
   * Track purchase - when a user completes a purchase
   * @param data - Purchase data
   */
  trackPurchase(data: {
    itemIds: string[];
    profileKey?: string;
    revenue?: number;
    sessionKey?: string;
  }): void {
    this.pushEvent({
      event: 'purchase',
      data,
    });
  }

  /**
   * Track search - when a user performs a search
   * @param data - Search data
   */
  trackSearch(data: {
    searchTerm: string;
    profileKey?: string;
    sessionKey?: string;
  }): void {
    this.pushEvent({
      event: 'search',
      data,
    });
  }

  /**
   * Track session start - when a new session begins
   * @param data - Session start data
   */
  trackSessionStart(data: {
    profileKey?: string;
    sessionKey?: string;
  }): void {
    this.pushEvent({
      event: 'sessionStart',
      data,
    });
  }

  /**
   * Get the profile key if available
   */
  getProfileKey(): string | null {
    if (this.isAvailable() && typeof window.tweakwiseLayer!.getProfileKey === 'function') {
      return window.tweakwiseLayer!.getProfileKey() || null;
    }
    return null;
  }
}

const tweakwiseTracker = new TweakwiseTracker();

export { tweakwiseTracker };

